import { Modal, Select, Tabs } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import Loader from '@/components/Atoms/Loader/Loader'
import CharacteristicCreationModal from '@/components/EntityCreationModals/CharacteristicCreationModal/CharacteristicCreationModal'
import TreeEntityCreationModal from '@/components/EntityCreationModals/TreeEntityCreationModal/TreeEntityCreationModal'
import { formatCharacteristicValue } from '@/components/Filters/Filters.utils'
import { TYPE_LABELS } from '@/constants'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { CategoryInterface } from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { isNomenclatureEditor } from '@/utils/auth/userRoles'
import { isCatalogApprover } from '@/utils/catalog/approvalStatus'
import { cleanTechnicalSuffix } from '@/utils/catalog/cleanDescription'

import type { AvailableCharacteristic } from '../../../../AttributeProcessing.types'
import { useAttributeProcessingContext } from '../../../../AttributeProcessingContext'
import CreationCategoriesTable from '../CreationCategoriesTable/CreationCategoriesTable'
import CreationCharacteristicsTable from '../CreationCharacteristicsTable/CreationCharacteristicsTable'
import cn from './CreationTab.module.scss'

type CreationSubTab = 'CATEGORY' | 'CHARACTERISTIC' | 'VALUES'

type CreationResult = {
  kind: CreationSubTab
  name: string
  message: string
}

type CharacteristicValue = {
  id: string
  value: string
}

type CharacteristicValuesPanelProps = {
  availableCharacteristics: AvailableCharacteristic[]
  characteristic: AvailableCharacteristic | null
  onSelectCharacteristic: (
    characteristic: AvailableCharacteristic | null
  ) => void
  modal: ReturnType<typeof Modal.useModal>[0]
}

function CharacteristicValuesPanel(props: CharacteristicValuesPanelProps) {
  const {
    availableCharacteristics,
    characteristic,
    onSelectCharacteristic,
    modal,
  } = props
  const [items, setItems] = useState<CharacteristicValue[]>([])
  const [valueSearch, setValueSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [newValue, setNewValue] = useState('')
  const [editingValueId, setEditingValueId] = useState('')
  const [editingValue, setEditingValue] = useState('')
  const [actionKey, setActionKey] = useState('')

  useEffect(() => {
    if (!characteristic || characteristic.type !== 'REFERENCE') {
      setItems([])
      setLoading(false)
      setNewValue('')
      setValueSearch('')
      setEditingValueId('')
      setEditingValue('')
      return
    }

    let isActive = true

    setLoading(true)
    setItems([])
    setNewValue('')
    setValueSearch('')
    setEditingValueId('')
    setEditingValue('')

    api
      .get(`/main/characteristics/${characteristic.id}`)
      .then((response) => {
        if (!isActive) return
        setItems(response.data.items ?? [])
      })
      .catch((error) => {
        if (!isActive) return
        handleApiError(error, modal)
      })
      .finally(() => {
        if (isActive) setLoading(false)
      })

    return () => {
      isActive = false
    }
  }, [characteristic])

  const unitLabel = characteristic?.unit?.trim() ?? ''
  const characteristicName = cleanTechnicalSuffix(characteristic?.name)
  const filteredItems = useMemo(() => {
    const normalizedSearch = valueSearch.trim().toLowerCase()

    return normalizedSearch
      ? items.filter((item) =>
          item.value.toLowerCase().includes(normalizedSearch)
        )
      : items
  }, [items, valueSearch])
  const characteristicTypeLabel = characteristic
    ? (TYPE_LABELS[characteristic.type] ?? characteristic.type)
    : ''

  const renderUnit = () =>
    unitLabel ? (
      <span className={cn.valueUnit}>Ед. изм.: {unitLabel}</span>
    ) : null

  const handleAddValue = async () => {
    if (!characteristic) return

    const trimmedValue = newValue.trim()
    if (!trimmedValue) {
      toast.error('Заполните значение')
      return
    }

    setActionKey('create')
    try {
      const response = await api.post(
        `/main/characteristics/${characteristic.id}/values`,
        { value: trimmedValue }
      )

      setItems((prev) => [...prev, response.data])
      setNewValue('')
      toast.success('Значение добавлено')
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setActionKey('')
    }
  }

  const handleSaveValue = async () => {
    const trimmedValue = editingValue.trim()

    if (!editingValueId || !trimmedValue) {
      toast.error('Заполните значение')
      return
    }

    setActionKey(`update-${editingValueId}`)
    try {
      const response = await api.put(
        `/main/characteristics/values/${editingValueId}`,
        { value: trimmedValue }
      )

      setItems((prev) =>
        prev.map((item) => (item.id === editingValueId ? response.data : item))
      )
      setEditingValueId('')
      setEditingValue('')
      toast.success('Значение обновлено')
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setActionKey('')
    }
  }

  const handleDeleteValue = async (valueId: string) => {
    setActionKey(`delete-${valueId}`)
    try {
      await api.delete(`/main/characteristics/values/${valueId}`)
      setItems((prev) => prev.filter((item) => item.id !== valueId))
      if (editingValueId === valueId) {
        setEditingValueId('')
        setEditingValue('')
      }
      toast.success('Значение удалено')
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setActionKey('')
    }
  }

  return (
    <div className={cn.valuesCard}>
      <div className={cn.valueSelectGroup}>
        <div className={cn.valuesMetaLabel}>Характеристика</div>
        <Select
          showSearch
          allowClear
          className={cn.characteristicSelect}
          placeholder="Выберите характеристику"
          optionFilterProp="label"
          value={characteristic?.id}
          onChange={(nextId) => {
            const nextCharacteristic =
              availableCharacteristics.find((item) => item.id === nextId) ??
              null

            onSelectCharacteristic(nextCharacteristic)
          }}
          options={availableCharacteristics.map((item) => ({
            value: item.id,
            label: `${cleanTechnicalSuffix(item.name)}${item.unit ? ` (${item.unit})` : ''}`,
          }))}
        />
      </div>

      {!characteristic ? (
        <div className={cn.sectionText}>
          Выберите характеристику, чтобы управлять её значениями.
        </div>
      ) : characteristic.type !== 'REFERENCE' ? (
        <div className={cn.sectionText}>
          Для характеристики <strong>{characteristicName}</strong> отдельный
          справочник значений не используется. Тип характеристики:{' '}
          <strong>{characteristicTypeLabel}</strong>.
        </div>
      ) : (
        <>
          <div className={cn.valuesMeta}>
            <span className={cn.valuesMetaLabel}>Выбрана характеристика:</span>
            <span className={cn.valuesMetaName}>{characteristicName}</span>
            {renderUnit()}
          </div>

          <div className={cn.valueCreateRow}>
            <div className={cn.valueInputGroup}>
              <input
                value={newValue}
                placeholder="Новое значение"
                onChange={(event) => setNewValue(event.target.value)}
              />
              {renderUnit()}
            </div>
            <button
              type="button"
              className={cn.secondaryButton}
              disabled={actionKey === 'create'}
              onClick={() => void handleAddValue()}
            >
              {actionKey === 'create' ? 'Добавление...' : 'Добавить значение'}
            </button>
          </div>

          <input
            className={cn.valueSearch}
            value={valueSearch}
            placeholder="Поиск значений"
            onChange={(event) => setValueSearch(event.target.value)}
          />

          {loading ? (
            <div className={cn.valuesLoader}>
              <Loader size={18} />
            </div>
          ) : filteredItems.length > 0 ? (
            <div className={cn.valuesList}>
              {filteredItems.map((item) => {
                const isEditing = editingValueId === item.id
                const isSaving = actionKey === `update-${item.id}`
                const isDeleting = actionKey === `delete-${item.id}`

                return (
                  <div key={item.id} className={cn.valueItemRow}>
                    {isEditing ? (
                      <>
                        <div className={cn.valueInputGroup}>
                          <input
                            value={editingValue}
                            onChange={(event) =>
                              setEditingValue(event.target.value)
                            }
                          />
                          {renderUnit()}
                        </div>
                        <div className={cn.valueActions}>
                          <button
                            type="button"
                            className={cn.primaryButton}
                            disabled={isSaving}
                            onClick={() => void handleSaveValue()}
                          >
                            {isSaving ? 'Сохранение...' : 'Сохранить'}
                          </button>
                          <button
                            type="button"
                            className={cn.ghostButton}
                            onClick={() => {
                              setEditingValueId('')
                              setEditingValue('')
                            }}
                          >
                            Отмена
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={cn.valueItemTextWrap}>
                          <span className={cn.valueItemText}>
                            {formatCharacteristicValue(item.value)}
                          </span>
                          {renderUnit()}
                        </div>
                        <div className={cn.valueActions}>
                          <button
                            type="button"
                            className={cn.secondaryButton}
                            onClick={() => {
                              setEditingValueId(item.id)
                              setEditingValue(item.value)
                            }}
                          >
                            Изменить
                          </button>
                          <button
                            type="button"
                            className={cn.dangerButton}
                            disabled={isDeleting}
                            onClick={() => void handleDeleteValue(item.id)}
                          >
                            {isDeleting ? 'Удаление...' : 'Удалить'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className={cn.sectionText}>
              {items.length > 0
                ? 'Значения не найдены.'
                : 'У этой характеристики пока нет значений. Можно добавить первое.'}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function CreationTab() {
  const {
    handleTabChange,
    loadCategories,
    loadCharacteristicsTree,
    refreshCategoriesTree,
    refreshCharacteristicsTree,
    globalCategoriesTree,
    categoriesLoading,
    availableCharacteristics,
    characteristicsLoading,
  } = useAttributeProcessingContext()
  const currentUser = useUserInfoStore((state) => state.currentUser)
  const isEditor = useUserInfoStore((state) =>
    isNomenclatureEditor(state.tokenRoles)
  )

  const [activeSubTab, setActiveSubTab] =
    useState<CreationSubTab>('CHARACTERISTIC')
  const [openCategoryModal, setOpenCategoryModal] = useState(false)
  const [openCharacteristicModal, setOpenCharacteristicModal] = useState(false)
  const [lastCreated, setLastCreated] = useState<CreationResult | null>(null)
  const [selectedCharacteristic, setSelectedCharacteristic] =
    useState<AvailableCharacteristic | null>(null)
  const hydratedRef = useRef(false)
  const [modal, contextHolder] = Modal.useModal()

  const isApprover = isCatalogApprover(currentUser?.roles)
  const isViewerOnly = isEditor && !isApprover

  useEffect(() => {
    if (hydratedRef.current) return

    hydratedRef.current = true
    void loadCategories()
    void loadCharacteristicsTree()
  }, [loadCategories, loadCharacteristicsTree])

  useEffect(() => {
    if (!selectedCharacteristic) return

    const nextSelected = availableCharacteristics.find(
      (item) => item.id === selectedCharacteristic.id
    )

    if (nextSelected) {
      setSelectedCharacteristic(nextSelected)
    }
  }, [availableCharacteristics, selectedCharacteristic])

  const handleCategoryCreated = async (created?: { description: string }) => {
    await refreshCategoriesTree()
    setLastCreated({
      kind: 'CATEGORY',
      name: created?.description || 'Новая категория',
      message:
        'Категория создана. Обновлённый список уже доступен в выборе категории и в фильтрах.',
    })
  }

  const handleCharacteristicCreated = async (created?: {
    description: string
  }) => {
    await refreshCharacteristicsTree()
    setLastCreated({
      kind: 'CHARACTERISTIC',
      name: created?.description || 'Новая характеристика',
      message: 'Характеристика создана. Справочник страницы обновлён.',
    })
  }

  const categoriesTree = globalCategoriesTree as CategoryInterface[]

  const handleCharacteristicUnitChange = async (
    characteristic: AvailableCharacteristic,
    nextUnitId: string
  ) => {
    try {
      await api.put(`/main/characteristics/${characteristic.id}`, {
        description: characteristic.name,
        useUnitOfMeasurement:
          characteristic.type !== 'BOOLEAN' && Boolean(nextUnitId),
        unitOfMeasurement:
          characteristic.type !== 'BOOLEAN' && nextUnitId
            ? nextUnitId
            : undefined,
      })
      await refreshCharacteristicsTree()
    } catch (error) {
      console.error(error)
      toast.error('Не удалось обновить единицу измерения')
      throw error
    }
  }

  return (
    <>
      {contextHolder}
      <CharacteristicCreationModal
        open={openCharacteristicModal}
        setOpen={setOpenCharacteristicModal}
        onSuccess={(created) => {
          void handleCharacteristicCreated(created)
        }}
      />

      <TreeEntityCreationModal
        open={openCategoryModal}
        setOpen={setOpenCategoryModal}
        treeType="categories"
        onSuccess={(created) => {
          void handleCategoryCreated(created)
        }}
      />

      <div className={cn.creationTab}>
        <div className={cn.headerBlock}>
          <div className={cn.headerTitle}>Создание</div>
          <div className={cn.headerText}>
            Создайте категорию или характеристику, не переходя на отдельные
            страницы.
          </div>
          {isViewerOnly && (
            <div className={cn.infoText}>
              Ниже доступны существующие категории и характеристики для
              просмотра. Редактирование и согласование выполняются только
              пользователем с правом согласования.
            </div>
          )}
        </div>

        <Tabs
          activeKey={activeSubTab}
          onChange={(key) => setActiveSubTab(key as CreationSubTab)}
          className={cn.innerTabs}
          items={[
            {
              key: 'CHARACTERISTIC',
              label: 'Характеристика',
              children: (
                <div className={cn.actionCard}>
                  <div className={cn.cardTitle}>Создание характеристики</div>
                  <div className={cn.cardText}>
                    Откроется форма со всеми полями из каталога характеристик:
                    тип значения, единица измерения и мгновенное утверждение при
                    наличии прав.
                  </div>
                  <button
                    className={cn.primaryButton}
                    onClick={() => setOpenCharacteristicModal(true)}
                  >
                    Создать характеристику
                  </button>
                  <div className={cn.sectionText}>
                    Здесь отображается общий список, включая несогласованные
                    записи и их статусы. Доступно только изменение единицы
                    измерения.
                  </div>
                  <CreationCharacteristicsTable
                    data={availableCharacteristics}
                    loading={characteristicsLoading}
                    onUnitChange={handleCharacteristicUnitChange}
                    selectedCharacteristicId={selectedCharacteristic?.id}
                    onSelectCharacteristic={setSelectedCharacteristic}
                  />
                </div>
              ),
            },
            {
              key: 'VALUES',
              label: 'Значения',
              children: (
                <div className={cn.actionCard}>
                  <div className={cn.cardTitle}>Значения характеристик</div>
                  <div className={cn.cardText}>
                    Выберите характеристику из списка ниже и управляйте её
                    значениями: можно добавить новое значение или изменить
                    существующее без перехода в карточку характеристики.
                  </div>
                  <CharacteristicValuesPanel
                    availableCharacteristics={availableCharacteristics}
                    characteristic={selectedCharacteristic}
                    onSelectCharacteristic={setSelectedCharacteristic}
                    modal={modal}
                  />
                </div>
              ),
            },
            {
              key: 'CATEGORY',
              label: 'Категория',
              children: (
                <div className={cn.actionCard}>
                  <div className={cn.cardTitle}>Создание категории</div>
                  <div className={cn.cardText}>
                    Используется та же логика, что и в каталоге категорий: можно
                    выбрать родителя и сразу утвердить категорию при наличии
                    прав.
                  </div>
                  <button
                    className={cn.primaryButton}
                    onClick={() => setOpenCategoryModal(true)}
                  >
                    Создать категорию
                  </button>
                  <div className={cn.sectionText}>
                    Показывается полное дерево категорий со статусами. Это
                    справочный список без редактирования.
                  </div>
                  <CreationCategoriesTable
                    data={categoriesTree}
                    loading={categoriesLoading}
                  />
                </div>
              ),
            },
          ]}
        />

        {lastCreated && (
          <div className={cn.successCard}>
            <div className={cn.successLabel}>Создано</div>
            <div className={cn.successName}>{lastCreated.name}</div>
            <div className={cn.successText}>{lastCreated.message}</div>
            <div className={cn.successActions}>
              <button
                className={cn.secondaryButton}
                onClick={() =>
                  handleTabChange(
                    lastCreated.kind === 'CATEGORY' ? 'MAIN' : 'ADDITIONAL'
                  )
                }
              >
                {lastCreated.kind === 'CATEGORY'
                  ? 'К основным реквизитам'
                  : 'К доп. реквизитам'}
              </button>
              <button
                className={cn.ghostButton}
                onClick={() =>
                  lastCreated.kind === 'CATEGORY'
                    ? setOpenCategoryModal(true)
                    : setOpenCharacteristicModal(true)
                }
              >
                Создать ещё
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default CreationTab
